import { useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { toast } from 'sonner';

import { getDuoChatOptions } from '@/features/chat/hooks/useGetDuoChat';
import { playSoundWithSettings } from '@/features/sounds/utils/playSoundWithSettings';
import {
  useIsStreamChatClientConnected,
  useStreamChatClient,
} from '@/integrations/getstream/chat-client';
import { getErrorMessageSync } from '@/shared/utils/errorMessage';

export function useSendDuoMessage() {
  const queryClient = useQueryClient();
  const client = useStreamChatClient();
  const isConnected = useIsStreamChatClientConnected();
  const [isLoading, setIsLoading] = useState(false);

  const sendMessage = async (
    userId: string,
    messageText: string,
    options?: { skipToast?: boolean },
  ): Promise<boolean> => {
    if (!messageText.trim()) return false;

    setIsLoading(true);

    try {
      const duoChat = await queryClient.fetchQuery(
        getDuoChatOptions({ duoId: userId }),
      );

      const channel = client.channel(
        duoChat.getstreamChannel.type,
        duoChat.getstreamChannel.id,
      );

      await channel.sendMessage({
        text: messageText,
      });

      playSoundWithSettings(queryClient, 'send');
      if (!options?.skipToast) {
        toast.success('Message sent successfully');
      }
      return true;
    } catch (error) {
      toast.error(`Failed to send message: ${getErrorMessageSync(error)}`);
      return false;
    } finally {
      setIsLoading(false);
    }
  };

  return {
    sendMessage,
    isLoading,
    isReady: isConnected,
  };
}
